import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDateTime } from "../lib/format";
import { Modal, Field, Badge, Spinner, EmptyState, Confirm } from "../components/ui";
import { SearchSelect, orgOptions } from "../components/SearchSelect";

const PRIORITES = ["P1", "P2", "P3", "P4"];
const STATUTS = ["Nouveau", "EnCours", "EnAttente", "Resolu", "Ferme"];
const STATUT_LABELS: Record<string, string> = {
  Nouveau: "Nouveau", EnCours: "En cours", EnAttente: "En attente", Resolu: "Résolu", Ferme: "Fermé",
};
const TYPES = ["Support", "Demande", "Incident", "AVV", "Sales", "Déploiement"];

function SlaBadge({ sla }: { sla: any }) {
  if (!sla) return <span className="text-xs text-muted">—</span>;
  if (sla.etat === "tenu") return <Badge className="bg-green text-white">SLA tenu</Badge>;
  if (sla.etat === "depasse_resolu") return <Badge className="bg-red-50 text-danger">Résolu hors SLA</Badge>;
  if (sla.etat === "depasse") return <Badge className="bg-red-50 text-danger">Dépassé de {Math.abs(sla.restant_h).toFixed(0)} h</Badge>;
  const cls = sla.etat === "ambre" ? "bg-amber-50 text-amber" : "bg-soft text-muted";
  return <Badge className={cls}>Reste {sla.restant_h?.toFixed(0)} h</Badge>;
}

function ContratPicker({ contrats, value, onChange }: any) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = contrats.find((c: any) => c.id === value);
  const ql = q.trim().toLowerCase();
  const filtered = contrats
    .filter((c: any) => !ql || `${c.reference || ""} ${c.client_nom || ""} ${c.revendeur_nom || ""} ${c.id_editeur || ""}`.toLowerCase().includes(ql))
    .slice(0, 40);
  return (
    <div className="relative">
      <button type="button" data-testid="ticket-contrat-trigger" className="nx-input w-full text-left" onClick={() => setOpen((o) => !o)}>
        {selected ? `${selected.reference || "Contrat"}${selected.client_nom ? " — " + selected.client_nom : ""}` : <span className="text-muted">Aucun contrat lié</span>}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line bg-white shadow-lg">
            <input data-testid="ticket-contrat-search" autoFocus className="nx-input !rounded-none !border-x-0 !border-t-0" placeholder="Rechercher (réf., client, ID éditeur…)" value={q} onChange={(e) => setQ(e.target.value)} />
            <div className="max-h-56 overflow-y-auto">
              <button type="button" data-testid="ticket-contrat-opt-none" className="block w-full px-3 py-2 text-left text-sm hover:bg-soft" onClick={() => { onChange(null); setOpen(false); setQ(""); }}>Aucun contrat lié</button>
              {filtered.map((c: any) => (
                <button key={c.id} type="button" data-testid={`ticket-contrat-opt-${c.reference || c.id}`} className="block w-full px-3 py-2 text-left text-sm hover:bg-soft" onClick={() => { onChange(c.id); setOpen(false); setQ(""); }}>
                  <span className="font-semibold text-green">{c.reference || "Contrat"}</span>{c.client_nom ? ` — ${c.client_nom}` : ""}{c.id_editeur ? <span className="text-muted"> · {c.id_editeur}</span> : ""}
                </button>
              ))}
              {filtered.length === 0 && <p className="px-3 py-2 text-xs text-muted">Aucun contrat trouvé</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ContratInfo({ contrat, testId }: any) {
  if (!contrat) return null;
  return (
    <div className="col-span-2 rounded-lg bg-soft px-3 py-2 text-xs" data-testid={testId || "ticket-contrat-info"}>
      <div className="grid grid-cols-3 gap-2">
        <div><span className="block font-bold uppercase text-muted">ID éditeur</span>{contrat.id_editeur || "—"}</div>
        <div><span className="block font-bold uppercase text-muted">Revendeur</span>{contrat.revendeur_nom || "—"}</div>
        <div><span className="block font-bold uppercase text-muted">Client final</span>{contrat.client_nom || "—"}</div>
      </div>
    </div>
  );
}

function TicketFiche({ ticketId, users, onClose, reload }: any) {
  const [t, setT] = useState<any>(null);
  const [msg, setMsg] = useState("");
  const [kind, setKind] = useState("interne");
  const [file, setFile] = useState<File | null>(null);
  const load = () => api.get(`/tickets/${ticketId}`).then((r) => setT(r.data)).catch((e) => { alert(apiError(e)); onClose(); });
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);
  if (!t) return null;

  const patch = async (p: any) => {
    await api.patch(`/tickets/${t.id}`, p);
    load();
    reload();
  };

  const addMsg = async () => {
    if (kind === "piece") {
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("texte", msg);
      await api.post(`/tickets/${t.id}/pieces`, fd);
      setFile(null);
    } else {
      if (!msg.trim()) return;
      await api.post(`/tickets/${t.id}/messages`, { kind, texte: msg });
    }
    setMsg("");
    load();
  };

  const KIND_STYLE: Record<string, string> = {
    open: "border-green bg-lime-soft",
    interne: "border-line bg-soft/60",
    client: "border-info/30 bg-blue-50",
    piece: "border-amber/30 bg-amber-50",
  };
  const KIND_LABEL: Record<string, string> = { open: "Ouverture", interne: "Note interne", client: "Réponse client", piece: "Pièce jointe" };

  return (
    <Modal title={`${t.reference} — ${t.objet}`} onClose={onClose} wide testId="ticket-fiche">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge className="bg-green text-white">{t.type}</Badge>
        {t.priorite && <Badge className={t.priorite === "P1" ? "bg-red-50 text-danger" : "bg-soft text-green"}>{t.priorite}</Badge>}
        <SlaBadge sla={t.sla} />
        <Badge className="bg-soft text-muted">{t.client_nom}</Badge>
        <div className="ml-auto flex gap-2">
          <select data-testid="ticket-statut-select" className="nx-input !w-auto !py-1 text-xs" value={t.statut} onChange={(e) => patch({ statut: e.target.value })}>
            {STATUTS.map((s) => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
          </select>
          <select data-testid="ticket-assigne-select" className="nx-input !w-auto !py-1 text-xs" value={t.assigne_id || ""} onChange={(e) => patch({ assigne_id: e.target.value || null })}>
            <option value="">Non assigné</option>
            {users.map((u: any) => <option key={u.id} value={u.id}>{u.nom_complet}</option>)}
          </select>
        </div>
      </div>
      {t.contrat_id && (
        <div className="mb-3 rounded-lg border border-line bg-soft/60 px-3 py-2 text-xs" data-testid="ticket-fiche-contrat">
          <span className="font-bold text-green">Contrat lié : {t.contrat_reference || "—"}</span>
          <div className="mt-1 grid grid-cols-3 gap-2">
            <div><span className="block font-bold uppercase text-muted">ID éditeur</span>{t.contrat_id_editeur || "—"}</div>
            <div><span className="block font-bold uppercase text-muted">Revendeur</span>{t.contrat_revendeur_nom || "—"}</div>
            <div><span className="block font-bold uppercase text-muted">Client final</span>{t.contrat_client_nom || "—"}</div>
          </div>
        </div>
      )}
      <div className="mb-3 max-h-64 space-y-2 overflow-y-auto" data-testid="ticket-messages">
        {(t.messages || []).map((m: any) => (
          <div key={m.id} className={`rounded-lg border px-3 py-2 text-sm ${KIND_STYLE[m.kind]}`}>
            <div className="mb-0.5 flex justify-between text-[11px] text-muted">
              <span className="font-bold">{KIND_LABEL[m.kind]} · {m.auteur_nom}</span>
              <span>{fmtDateTime(m.created_at)}</span>
            </div>
            <p>{m.texte}</p>
            {m.fichier_nom && <p className="mt-1 text-xs font-semibold text-amber">{m.fichier_nom}</p>}
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <div className="flex gap-2">
          <select data-testid="ticket-msg-kind-select" className="nx-input !w-40" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="interne">Note interne</option>
            <option value="client">Réponse client</option>
            <option value="piece">Pièce jointe</option>
          </select>
          {kind === "piece" && (
            <input data-testid="ticket-file-input" type="file" className="nx-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          )}
        </div>
        <div className="flex gap-2">
          <input data-testid="ticket-msg-input" className="nx-input" placeholder="Votre message…" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addMsg()} />
          <button data-testid="ticket-msg-send-btn" className="nx-btn-primary" onClick={addMsg}>Ajouter</button>
        </div>
      </div>
    </Modal>
  );
}

export default function Tickets() {
  const { user } = useAuth();
  const mgr = user?.role === "manager";
  const [tickets, setTickets] = useState<any[] | null>(null);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [produits, setProduits] = useState<any[]>([]);
  const [contrats, setContrats] = useState<any[]>([]);
  const [fiche, setFiche] = useState<string | null>(null);
  const [edit, setEdit] = useState<any>(null);
  const [del, setDel] = useState<any>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();

  const load = (archived = showArchived) =>
    api.get(`/tickets${archived ? "?archived=true" : ""}`).then((r) => setTickets(r.data));

  useEffect(() => {
    api.get("/organisations").then((r) => setOrgs(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/produits").then((r) => setProduits(r.data));
    api.get("/contrats").then((r) => setContrats(r.data)).catch(() => setContrats([]));
  }, []);

  useEffect(() => {
    load(showArchived);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  useEffect(() => {
    const tk = params.get("ticket");
    if (tk) setFiche(tk);
    if (params.get("new")) setEdit({ type: "Support", statut: "Nouveau" });
    if (tk || params.get("new")) {
      params.delete("ticket");
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  if (!tickets) return <Spinner />;

  const save = async () => {
    setErr(null);
    try {
      const payload: any = {
        objet: edit.objet,
        type: edit.type,
        priorite: edit.priorite || null,
        produit_id: edit.produit_id || null,
        client_id: edit.client_id || null,
        assigne_id: edit.assigne_id || null,
        lien_label: edit.lien_label || null,
        contrat_id: edit.contrat_id || null,
        description: edit.description || null,
        statut: edit.statut || "Nouveau",
      };
      if (edit.id) await api.patch(`/tickets/${edit.id}`, payload);
      else await api.post("/tickets", payload);
      setEdit(null);
      load();
    } catch (e) {
      setErr(apiError(e));
    }
  };

  const archive = async (t: any) => {
    await api.delete(`/tickets/${t.id}`);
    setDel(null);
    load();
  };
  const reactivate = async (t: any) => {
    await api.post(`/tickets/${t.id}/reactiver`);
    load();
  };

  const selectedContrat = edit ? contrats.find((c) => c.id === edit.contrat_id) : null;

  return (
    <div data-testid="tickets-page">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-extrabold text-green">Tickets {showArchived && <span className="text-base font-semibold text-muted">· archivés</span>}</h1>
        <div className="flex gap-2">
          <button data-testid="ticket-toggle-archived-btn" className={showArchived ? "nx-btn-primary" : "nx-btn-ghost"} onClick={() => setShowArchived((v) => !v)}>
            {showArchived ? "Afficher les actifs" : "Afficher les archivés"}
          </button>
          {!showArchived && <button data-testid="ticket-new-btn" className="nx-btn-primary" onClick={() => setEdit({ type: "Support", statut: "Nouveau" })}>Nouveau ticket</button>}
        </div>
      </div>
      <div className="nx-card overflow-x-auto">
        {tickets.length === 0 ? <EmptyState message={showArchived ? "Aucun ticket archivé" : "Aucun ticket"} /> : (
          <table className="nx-table">
            <thead><tr><th>Réf.</th><th>Objet</th><th>Type</th><th>Produit</th><th>Priorité</th><th>SLA</th><th>Statut</th><th>Client</th><th>Contrat</th><th>Assigné</th><th>Créé le</th><th className="text-right">Actions</th></tr></thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id} data-testid={`ticket-row-${t.reference}`}>
                  <td><button data-testid={`ticket-open-${t.reference}`} className="font-extrabold text-green underline-offset-2 hover:underline" onClick={() => setFiche(t.id)}>{t.reference}</button></td>
                  <td className="max-w-[240px] truncate">{t.objet}</td>
                  <td>{t.type}</td>
                  <td>{t.produit_nom}</td>
                  <td>{t.priorite && <Badge className={t.priorite === "P1" ? "bg-red-50 text-danger" : "bg-soft text-green"}>{t.priorite}</Badge>}</td>
                  <td><SlaBadge sla={t.sla} /></td>
                  <td><Badge className={t.statut === "Resolu" || t.statut === "Ferme" ? "bg-lime-soft text-green" : "bg-blue-50 text-info"}>{STATUT_LABELS[t.statut] || t.statut}</Badge></td>
                  <td>{t.client_nom}</td>
                  <td className="text-xs">{t.contrat_reference || "—"}</td>
                  <td>{t.assigne_nom}</td>
                  <td className="text-xs text-muted">{fmtDateTime(t.created_at)}</td>
                  <td className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {!showArchived && <button data-testid={`ticket-edit-btn-${t.reference}`} className="nx-btn-ghost !px-2 !py-1 text-xs" onClick={() => setEdit({ ...t })}>Éditer</button>}
                      {mgr && !showArchived && <button data-testid={`ticket-archive-btn-${t.reference}`} className="nx-btn-danger !px-2 !py-1 text-xs" onClick={() => setDel(t)}>Archiver</button>}
                      {mgr && showArchived && <button data-testid={`ticket-reactivate-btn-${t.reference}`} className="nx-btn-lime !px-2 !py-1 text-xs" onClick={() => reactivate(t)}>Réactiver</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {edit && (
        <Modal title={edit.id ? `Modifier le ticket ${edit.reference}` : "Nouveau ticket"} onClose={() => setEdit(null)} testId="ticket-modal">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Objet" className="col-span-2">
              <input data-testid="ticket-objet-input" className="nx-input" value={edit.objet || ""} onChange={(e) => setEdit({ ...edit, objet: e.target.value })} />
            </Field>
            <Field label="Type">
              <select data-testid="ticket-type-select" className="nx-input" value={edit.type} onChange={(e) => setEdit({ ...edit, type: e.target.value })}>
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Priorité">
              <select data-testid="ticket-priorite-select" className="nx-input" value={edit.priorite || ""} onChange={(e) => setEdit({ ...edit, priorite: e.target.value || null })}>
                <option value="">—</option>
                {PRIORITES.map((p) => <option key={p}>{p}</option>)}
              </select>
            </Field>
            {edit.id && (
              <Field label="Statut">
                <select data-testid="ticket-edit-statut-select" className="nx-input" value={edit.statut || "Nouveau"} onChange={(e) => setEdit({ ...edit, statut: e.target.value })}>
                  {STATUTS.map((s) => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
                </select>
              </Field>
            )}
            <Field label="Produit">
              <select className="nx-input" value={edit.produit_id || ""} onChange={(e) => setEdit({ ...edit, produit_id: e.target.value || null })}>
                <option value="">—</option>
                {produits.map((p) => <option key={p.id} value={p.id}>{p.nom}</option>)}
              </select>
            </Field>
            <Field label="Client">
              <SearchSelect testId="ticket-client-select" options={orgOptions(orgs)} value={edit.client_id || null} onChange={(v) => setEdit({ ...edit, client_id: v })} />
            </Field>
            <Field label="Assigné à">
              <select data-testid="ticket-assigne-new-select" className="nx-input" value={edit.assigne_id || ""} onChange={(e) => setEdit({ ...edit, assigne_id: e.target.value || null })}>
                <option value="">—</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.nom_complet}</option>)}
              </select>
            </Field>
            <Field label="Contrat lié" className="col-span-2">
              <ContratPicker contrats={contrats} value={edit.contrat_id || null} onChange={(id: string | null) => setEdit({ ...edit, contrat_id: id })} />
            </Field>
            {selectedContrat && <ContratInfo contrat={selectedContrat} testId="ticket-modal-contrat-info" />}
            <Field label="Élément lié (libellé)" className="col-span-2">
              <input className="nx-input" value={edit.lien_label || ""} onChange={(e) => setEdit({ ...edit, lien_label: e.target.value })} placeholder="Devis 472, Contrat C-…" />
            </Field>
            <Field label="Description" className="col-span-2">
              <textarea data-testid="ticket-desc-input" className="nx-input" rows={3} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
            </Field>
          </div>
          {err && <p className="mt-2 text-sm text-danger">{err}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button className="nx-btn-ghost" onClick={() => setEdit(null)}>Annuler</button>
            <button data-testid="ticket-save-btn" className="nx-btn-primary" disabled={!edit.objet} onClick={save}>{edit.id ? "Enregistrer" : "Créer"}</button>
          </div>
        </Modal>
      )}

      {del && (
        <Confirm
          message={`Archiver le ticket ${del.reference} ? Il pourra être réactivé depuis « Afficher les archivés ».`}
          onConfirm={() => archive(del)}
          onCancel={() => setDel(null)}
        />
      )}

      {fiche && <TicketFiche ticketId={fiche} users={users} reload={() => load()} onClose={() => setFiche(null)} />}
    </div>
  );
}
